import type { ReactNode } from 'react'
import { Button, type ZapButtonTone, type ZapButtonVariant } from './Button'

export interface ActionBarItem {
  key?: string
  label: string
  variant?: ZapButtonVariant
  tone?: ZapButtonTone
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  icon?: ReactNode
  ariaLabel?: string
  testId?: string
}

export interface ActionBarProps {
  /** 1 to 3 actions rendered in order. Primary action is placed last (right). */
  actions: ActionBarItem[]
  /** Optional leading content (e.g., net summary "Net +2", or status caption) */
  leadContent?: ReactNode
  /** When true (default), docked with sticky bottom positioning and safe area padding */
  docked?: boolean
  /** When true, renders an accompanying spacer so scrollable content is not obscured */
  spacer?: boolean
  className?: string
  testId?: string
}

/**
 * ActionBarSpacer — companion spacer ensuring scrollable content is not obscured by a docked ActionBar.
 */
export function ActionBarSpacer({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={`h-16 w-full flex-none pointer-events-none ${className ?? ''}`}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    />
  )
}

/**
 * ActionBar — docked action bar positioned above the mobile bottom tab bar.
 * Built from Common Button, hosting 1–3 actions with the primary button last (right),
 * safe-area bottom padding, and non-obscuring scroll spacing.
 */
export function ActionBar({
  actions,
  leadContent,
  docked = true,
  spacer = false,
  className,
  testId,
}: ActionBarProps) {
  // Cap at 3 actions
  const displayActions = actions.slice(0, 3)

  const bar = (
    <div
      data-testid={testId}
      className={`w-full bg-[var(--surface-card)] border-t border-[var(--border)] px-4 py-2.5 flex items-center gap-2.5 ${
        docked ? 'sticky bottom-0 z-10' : ''
      } ${className ?? ''}`}
      style={docked ? { paddingBottom: 'max(10px, env(safe-area-inset-bottom, 10px))' } : undefined}
    >
      {leadContent ? (
        <div className="flex-1 min-w-0 flex items-center text-xs text-[var(--muted)]">
          {leadContent}
        </div>
      ) : null}

      <div className={`flex items-center gap-2.5 ${leadContent ? 'flex-none' : 'w-full'}`}>
        {displayActions.map((action, index) => {
          const isLast = index === displayActions.length - 1
          const defaultVariant: ZapButtonVariant = isLast ? 'primary' : 'secondary'
          const variant = action.variant ?? defaultVariant

          return (
            <Button
              key={action.key ?? `${action.label}-${index}`}
              variant={variant}
              tone={action.tone}
              size="md"
              icon={action.icon}
              disabled={action.disabled}
              loading={action.loading}
              onClick={action.onClick}
              aria-label={action.ariaLabel}
              data-testid={action.testId}
              className={leadContent ? undefined : 'flex-1'}
            >
              {action.label}
            </Button>
          )
        })}
      </div>
    </div>
  )

  if (spacer && docked) {
    return (
      <>
        {bar}
        <ActionBarSpacer />
      </>
    )
  }

  return bar
}
